// SPDX-License-Identifier: LicenseRef-Proprietary

import { useCallback, useMemo } from 'react';
import {
  createBlankTree,
  createInfluenzaBlankSlate,
  type LegacyDecisionTree,
} from '../../../lib/tree';
import { fromLegacy } from '../../../lib/treeConversion';
import {
  isDecisionTreeJson,
  isLegacyDecisionTreeJson,
  toLegacyDecisionTree,
  type DecisionTreeJson,
  type LegacyDecisionTreeJson,
} from '../../../lib/treeCodec';
import { logActivity } from '../../../lib/storage';
import type { AppState } from '../../../store/vaccineClientStore';
import { useVaccineDashboardStore } from '../../../store/vaccineDashboardStore';
import type { TreeEdge, TreeNode } from '../../../lib/validation';
import { useAlerts } from '../../../hooks/useAlerts';

/**
 * Hook for decision tree operations (load, save, convert formats)
 * Extracted from DecisionTreeBuilder for better separation of concerns
 */
export function useDecisionTreeOperations(
  store: AppState
) {
  const { showError } = useAlerts();

  const isFiniteNumber = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value);

  const sanitizeNode = (node: TreeNode): TreeNode => {
    const children = Array.isArray(node.children)
      ? node.children.filter((id): id is string => typeof id === 'string')
      : [];

    return {
      ...node,
      label: typeof node.label === 'string' && node.label.trim().length > 0
        ? node.label
        : node.id,
      cost: isFiniteNumber(node.cost) ? node.cost : undefined,
      qaly: isFiniteNumber(node.qaly) ? node.qaly : undefined,
      children,
    };
  };

  const sanitizeEdge = (
    edge: TreeEdge,
    nodes: Record<string, TreeNode>,
  ): TreeEdge | null => {
    if (
      typeof edge.parentId !== 'string' ||
      typeof edge.childId !== 'string' ||
      !nodes[edge.parentId] ||
      !nodes[edge.childId]
    ) {
      return null;
    }

    const sanitized: TreeEdge = {
      parentId: edge.parentId,
      childId: edge.childId,
      label: edge.label,
    };

    if (isFiniteNumber(edge.probability)) {
      sanitized.probability = Math.min(Math.max(edge.probability, 0), 1);
    }
    if (isFiniteNumber(edge.transition_cost)) {
      sanitized.transition_cost = edge.transition_cost;
    }
    if (isFiniteNumber(edge.transition_utility_loss)) {
      sanitized.transition_utility_loss = Math.min(
        Math.max(edge.transition_utility_loss, 0),
        1,
      );
    }

    return sanitized;
  };

  const loadFromDecisionTreeJson = useCallback((data: DecisionTreeJson) => {
    const sanitizedNodes: Record<string, TreeNode> = {};
    Object.entries(data.nodes).forEach(([id, node]) => {
      sanitizedNodes[id] = sanitizeNode({ ...node, id });
    });

    const sanitizedEdges = data.edges
      .map((edge) => sanitizeEdge(edge, sanitizedNodes))
      .filter((edge): edge is TreeEdge => edge !== null);

    store.clearTree();

    Object.values(sanitizedNodes).forEach((node) => store.addNode(node));
    sanitizedEdges.forEach((edge) => store.addEdge(edge));

    store.setRootId(typeof data.rootId === 'string' ? data.rootId : null);

    if (data.view && typeof data.view === 'object') {
      store.setView(data.view as Partial<typeof store.view>);
    }

    store.setSelectedId(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  const loadFromLegacyTree = useCallback((data: LegacyDecisionTreeJson) => {
    const legacyTree = toLegacyDecisionTree(data);
    const { nodes: newNodes, edges: newEdges } = fromLegacy(legacyTree.root);
    loadFromDecisionTreeJson({
      title: legacyTree.title,
      rootId: legacyTree.root.id,
      nodes: newNodes,
      edges: newEdges,
    });
  }, [loadFromDecisionTreeJson]);

  const createBlank = useCallback(() => {
    const blankSlate = createInfluenzaBlankSlate();
    
    store.clearTree();
    const dashboardStore = useVaccineDashboardStore.getState();
    dashboardStore.clearBackendResults();
    // Sync dashboard inputs to match blank tree structure:
    // - No hospitalization/death nodes → rates = 0
    // - Infection probability = 0.20 (20%) → baselineIncidencePer100k = 20000
    // - Vaccine efficacy = (0.20 - 0.05) / 0.20 = 0.75 (75%)
    dashboardStore.patchInputs({
      hospitalizationRate: 0,
      baselineMortalityRate: 0,
      baselineIncidencePer100k: 20000, // 20% infection rate for non-vaccinated
      vaccineEfficacyPct: 75, // (0.20 - 0.05) / 0.20 = 75% reduction
    });
    // Also update vaccineClientStore population parameters to stay in sync
    // (prevents syncTreeParametersToVaccine from overwriting dashboard values)
    store.setPopulationParameters({
      baseline_incidence: 0.20, // 20% infection rate
      baseline_mortality: 0,
      hospitalization_rate: 0,
    });
    store.setRootId(blankSlate.rootId);
    
    // Add all nodes
    Object.values(blankSlate.nodes).forEach(node => store.addNode(node));
    
    // Add all edges
    blankSlate.edges.forEach(edge => store.addEdge(edge));
    
    store.setSelectedId(null);
    logActivity('Created blank slate decision tree');
  }, [store]);

  const loadTree = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const raw = JSON.parse(e.target?.result as string);

            if (isDecisionTreeJson(raw)) {
              loadFromDecisionTreeJson(raw);
              logActivity('Loaded decision tree (current format)');
            } else if (isLegacyDecisionTreeJson(raw)) {
              loadFromLegacyTree(raw);
              logActivity('Loaded decision tree (legacy format)');
            } else {
              throw new Error('Unsupported decision tree JSON format');
            }
          } catch (error) {
            console.error('Failed to load decision tree:', error);
            showError('Failed to load decision tree: Invalid JSON', 'Load Failed');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }, [loadFromDecisionTreeJson, loadFromLegacyTree, showError]);

  const saveTree = useCallback(() => {
    const treeState = store.getTreeState();
    const data: DecisionTreeJson = {
      title: 'Decision Tree',
      nodes: treeState.nodes,
      edges: treeState.edges,
      rootId: treeState.rootId,
      view: store.view,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `decision-tree-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    logActivity('Saved decision tree to JSON');
  }, [store]);

  // Convert new format to legacy format for compatibility with existing components
  const convertToLegacyTree = useCallback((): LegacyDecisionTree => {
    const { rootId, nodes } = store;
    if (!rootId || !nodes[rootId]) {
      return createBlankTree();
    }

    const edgeMap = new Map<string, TreeEdge>();
    store.edges.forEach((edge) => {
      edgeMap.set(`${edge.parentId}-${edge.childId}`, edge);
    });

    const convertNode = (nodeId: string): any => {
      const node = nodes[nodeId];
      if (!node) return null;

      const legacyNode: any = {
        id: node.id,
        type: node.type,
        label: node.label,
        cost_eur: node.cost,
        qaly: node.qaly,
        notes: node.notes,
        nodeType: (node as any).nodeType, // Preserve epidemiological outcome type
        children: []
      };

      // Add children
      node.children.forEach(childId => {
        const child = convertNode(childId);
        if (child) {
          const edge = edgeMap.get(`${node.id}-${childId}`);
          if (edge) {
            // Add probability for chance nodes
            if (edge.probability !== undefined) {
              child.p = edge.probability;
            }
            // Add transition cost if present
            if (edge.transition_cost !== undefined) {
              child.transition_cost = edge.transition_cost;
            }
            // Add transition utility loss if present
            if (edge.transition_utility_loss !== undefined) {
              child.transition_utility_loss = edge.transition_utility_loss;
            }
          }
          legacyNode.children.push(child);
        }
      });

      return legacyNode;
    };

    return {
      id: 'decision-tree',
      title: 'Decision Tree',
      root: convertNode(rootId)
    };
  }, [store]);

  // Memoize tree conversion for stable reference
  // Include a hash of node/edge values to detect cost/QALY changes
  const structureVersion = useMemo(() => {
    const nodeValues = Object.values(store.nodes)
      .map(n => `${n.id}:${n.cost}:${n.qaly}:${n.label}`)
      .join('|');
    const edgeValues = store.edges
      .map(e => `${e.parentId}-${e.childId}:${e.probability}:${e.transition_cost}:${e.transition_utility_loss}`)
      .join('|');
    return `${Object.keys(store.nodes).length}|${store.edges.length}|${store.rootId}|${nodeValues}|${edgeValues}`;
  }, [store.nodes, store.edges, store.rootId]);

  const legacyTree = useMemo(() => convertToLegacyTree(), [convertToLegacyTree]);

  return {
    createBlank,
    loadTree,
    saveTree,
    convertToLegacyTree,
    structureVersion,
    legacyTree
  };
}

